import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { user, login } = useAuth();
  const { t, lang, toggle } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setErr(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      {/* Left — brand pane */}
      <div
        className="hidden lg:flex flex-1 flex-col justify-between p-12"
        style={{ background: "var(--bg-elevated)", borderRight: "1px solid var(--border)" }}
      >
        <div>
          <div className="brand-mark">
            <span className="text-[22px] font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
              {t("app.name")}
            </span>
          </div>
          <div className="mt-1.5 text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {t("app.subtitle")}
          </div>
        </div>

        <div className="max-w-md">
          <h1
            className="text-[56px] font-semibold leading-[1.05] tracking-tight"
            style={{ color: "var(--text-primary)", letterSpacing: "-0.04em" }}
          >
            Rail<br />intelligence.
          </h1>
          <p className="mt-6 text-[17px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Live installed base, opportunity pipeline, account plans and competitor matrix —
            in one place.
          </p>
        </div>

        <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          Confidential — internal use only.
        </div>
      </div>

      {/* Right — login pane */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        <button
          onClick={toggle}
          className="btn-ghost absolute top-6 right-6 font-mono text-[11px]"
          data-testid="login-lang"
        >
          {lang.toUpperCase()} → {lang === "en" ? "FR" : "EN"}
        </button>

        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 brand-mark">
            <span className="text-[22px] font-semibold tracking-tight">{t("app.name")}</span>
          </div>

          <h2 className="text-[32px] font-semibold tracking-tight" style={{ letterSpacing: "-0.025em" }}>
            {t("login.title")}
          </h2>
          <p className="mt-2 text-[15px]" style={{ color: "var(--text-secondary)" }}>
            {t("login.subtitle")}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="label-tech block mb-1.5">{t("login.email")}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-tech"
                data-testid="login-email"
                placeholder="you@example.com"
                autoFocus
              />
            </div>
            <div>
              <label className="label-tech block mb-1.5">{t("login.password")}</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-tech"
                data-testid="login-password"
                placeholder="••••••••••••"
              />
            </div>
            {err && (
              <div
                className="text-[12px] font-mono p-3 rounded-[10px]"
                style={{
                  background: "rgba(237,32,39,0.08)",
                  color: "var(--accent)",
                  border: "1px solid rgba(237,32,39,0.2)",
                }}
                data-testid="login-error"
              >
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full inline-flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading ? t("common.loading") : t("login.submit")}
              <ArrowRight size={14} weight="bold" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
